/**
 * Step 8 Review & publish (§16.1): full validation summary (every issue links to its step and field), simulation
 * report (§5.5) placeholder that runs `POST /v1/admin/lotteries/:id/simulate`, second-person approval callout
 * (§5.4 dual control for paid / high-value), change note + remedy once entries exist, share link and QR code,
 * and the lifecycle actions (submit for review / approve / publish) for the holder's role.
 */
import { useMutation } from '@tanstack/react-query';
import { requiresDualControl, totalLiabilityMinor, type SimulationReportDto } from '@windrop/contracts';
import { Button, Field, Input, useIntl } from '@windrop/ui';
import QRCode from 'qrcode';
import { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormErrorSummary } from '../../../components/FormField';
import { ToneBadge } from '../../../components/StatusBadge';
import { lotteriesApi } from '../../../lib/api/admin';
import { isApiError } from '../../../lib/api/client';
import { readRuntimeConfig } from '../../../lib/runtime-config';
import { LifecycleActions } from '../lifecycle';
import { PublishGates, ShareLinksPanel } from '../LotteryOperations';
import { useWizard } from './WizardFrame';
import { DraftConfigSchema, stepForPath, type DraftConfigInput } from './steps';

export function ReviewStep() {
  const intl = useIntl();
  const { control } = useFormContext<DraftConfigInput>();
  const values = useWatch({ control });
  const {
    lottery,
    lotteryId,
    issues,
    goToStep,
    changeNote,
    setChangeNote,
    participantRemedy,
    setParticipantRemedy,
    autosave,
  } = useWizard();
  const { webOrigin } = readRuntimeConfig();
  const [qr, setQr] = useState<string | null>(null);
  const errors = issues.filter((i) => i.severity === 'error');
  const warnings = issues.filter((i) => i.severity === 'warning');
  const slug = (values.identity?.slug as string | undefined) ?? '';
  const shareUrl = slug ? `${webOrigin}/l/${slug}` : null;

  useEffect(() => {
    let cancelled = false;
    if (!shareUrl) {
      setQr(null);
      return;
    }
    QRCode.toDataURL(shareUrl, { margin: 1, width: 160 })
      .then((url) => {
        if (!cancelled) setQr(url);
      })
      .catch(() => {
        if (!cancelled) setQr(null);
      });
    return () => {
      cancelled = true;
    };
  }, [shareUrl]);

  const parsed = DraftConfigSchema.safeParse(values);
  const paid = parsed.success && parsed.data.entry.mode === 'PAID';
  const liability = parsed.success ? totalLiabilityMinor(parsed.data) : 0n;
  const dual = parsed.success
    ? requiresDualControl({
        action: 'lottery.publish',
        valueMinor: liability,
        paid,
        policy: { highValueThresholdMinor: DEFAULT_HIGH_VALUE_MINOR },
      })
    : false;

  const simulate = useMutation({ mutationFn: () => lotteriesApi.simulate(lotteryId ?? '', {}) });

  return (
    <div className="wd-stack">
      <section>
        <h2>Validation</h2>
        {errors.length === 0 ? (
          <ToneBadge tone="success">No blocking issues in the client preview</ToneBadge>
        ) : (
          <FormErrorSummary
            issues={errors}
            title={`${errors.length} blocking issue${errors.length === 1 ? '' : 's'}`}
            onNavigate={(p) => goToStep(stepForPath(p))}
          />
        )}
        {warnings.length ? (
          <details style={{ marginTop: 'var(--wd-space-2)' }}>
            <summary>
              {warnings.length} warning{warnings.length === 1 ? '' : 's'}
            </summary>
            <FormErrorSummary
              issues={warnings}
              title="Warnings (publish allowed)"
              onNavigate={(p) => goToStep(stepForPath(p))}
            />
          </details>
        ) : null}
        {lotteryId ? (
          <details open style={{ marginTop: 'var(--wd-space-3)' }}>
            <summary>Server publish gates (§5.4) for the saved version</summary>
            <PublishGates lotteryId={lotteryId} />
          </details>
        ) : null}
        <p className="wd-muted">
          The API re-validates on save and on publish; its result shows on the lottery overview. Liability in
          this draft:{' '}
          <strong>
            {parsed.success ? intl.formatMoney(liability.toString(), parsed.data.identity.currency) : '—'}
          </strong>
          .
        </p>
      </section>

      {lottery?.hasEntries ? (
        <section className="wdc-form-grid">
          <Field
            label="Change note"
            required
            hint="Why this version changes rules after entries exist (§5.4)."
            id="field-changeNote"
          >
            <Input value={changeNote} onChange={(e) => setChangeNote(e.target.value)} />
          </Field>
          <Field
            label="Participant remedy"
            required
            hint="What participants who entered under the previous version can do."
            id="field-participantRemedy"
          >
            <Input value={participantRemedy} onChange={(e) => setParticipantRemedy(e.target.value)} />
          </Field>
        </section>
      ) : null}

      <section>
        <h2>Simulation report</h2>
        <p className="wd-muted">
          Runs synthetic participants against this configuration (§5.5): expected entries, max prize
          liability, stock reconciliation, sampling allocation and copy previews. Never touches production
          inventory or ledgers.
        </p>
        <Button
          variant="secondary"
          onClick={() => simulate.mutate()}
          loading={simulate.isPending}
          loadingLabel="Simulating"
          disabled={!lotteryId}
          disabledReason={!lotteryId ? 'Save the draft first.' : undefined}
        >
          Run simulation
        </Button>
        {simulate.isError ? (
          <p role="alert" className="text-error">
            {isApiError(simulate.error) ? simulate.error.message : 'Simulation failed.'}
          </p>
        ) : null}
        {simulate.data ? (
          <SimulationReport
            report={simulate.data}
            currency={parsed.success ? parsed.data.identity.currency : 'XTS'}
          />
        ) : null}
      </section>

      <section
        className="wdc-callout"
        data-tone={dual ? 'warning' : 'success'}
        data-testid="approval-callout"
      >
        <p>
          <strong>{dual ? 'Second-person approval required.' : 'Single approval.'}</strong>{' '}
          {dual
            ? 'This lottery is paid or high-value: the person who submits cannot approve or publish it (§3, §5.4). Submit for review and ask a lottery approver.'
            : 'A lottery approver can approve and publish this configuration; the submitter still cannot approve their own work.'}
        </p>
        <p>Publishing commits the fairness seed (§9.1) and freezes this configuration version.</p>
      </section>

      <section>
        <h2>Share</h2>
        {lotteryId ? <ShareLinksPanel lotteryId={lotteryId} /> : null}
        {shareUrl && !lotteryId ? (
          <div className="wd-row" style={{ alignItems: 'flex-start', gap: 'var(--wd-space-4)' }}>
            {qr ? <img src={qr} alt={`QR code for ${shareUrl}`} width={160} height={160} /> : null}
            <div className="wd-stack">
              <p>
                Participant link: <a href={shareUrl}>{shareUrl}</a>
              </p>
              <p className="wd-muted">
                Per-platform links with UTM source are issued by the API once published; this QR points at the
                canonical page.
              </p>
            </div>
          </div>
        ) : (
          <p className="wd-muted">Set a slug on the Template step to get a link.</p>
        )}
      </section>

      <section>
        <h2>Lifecycle</h2>
        {lottery ? (
          <LifecycleActions
            lotteryId={lottery.lotteryId}
            slug={lottery.slug}
            status={lottery.status}
            affected={lottery.hasEntries ? undefined : { affected: 0, detail: 'no entries yet' }}
          />
        ) : (
          <p className="wd-muted">Save the draft to enable submit / approve / publish.</p>
        )}
        {autosave.status.kind === 'dirty' || autosave.status.kind === 'saving' ? (
          <p className="wd-muted">Waiting for autosave before lifecycle actions apply to the latest edits.</p>
        ) : null}
      </section>
    </div>
  );
}

/** Tenant threshold is an activation input (D-106); until settings ship, the console assumes 1,000.00 in minor units. */
export const DEFAULT_HIGH_VALUE_MINOR = 100_000n;

export function SimulationReport({ report, currency }: { report: SimulationReportDto; currency: string }) {
  const intl = useIntl();
  return (
    <div className="wd-stack" data-testid="simulation-report">
      <dl className="wdc-facts">
        <div>
          <dt>Expected entries</dt>
          <dd className="wd-tabular">{intl.formatNumber(report.expectedEntries)}</dd>
        </div>
        <div>
          <dt>Expected unique participants</dt>
          <dd className="wd-tabular">{intl.formatNumber(report.expectedUniqueParticipants)}</dd>
        </div>
        <div>
          <dt>Max prize liability</dt>
          <dd>
            {intl.formatMoney(
              report.maxPrizeLiability.amountMinor,
              report.maxPrizeLiability.currency || currency,
            )}
          </dd>
        </div>
        <div>
          <dt>Validation</dt>
          <dd>
            {report.validation.ok ? (
              <ToneBadge tone="success">OK</ToneBadge>
            ) : (
              <ToneBadge tone="error">{report.validation.issues.length} issues</ToneBadge>
            )}
          </dd>
        </div>
      </dl>
      <table className="wdc-table">
        <caption>Stock reconciliation</caption>
        <thead>
          <tr>
            <th scope="col">Prize</th>
            <th scope="col">Catalog</th>
            <th scope="col">Main reserved</th>
            <th scope="col">Sampling reserved</th>
            <th scope="col">Expected won</th>
            <th scope="col">Available</th>
          </tr>
        </thead>
        <tbody>
          {report.stockReconciliation.map((r) => (
            <tr key={r.prizeId}>
              <th scope="row">
                <code>{r.prizeId}</code>
              </th>
              <td className="wd-tabular">{r.catalogStock}</td>
              <td className="wd-tabular">{r.mainReserved}</td>
              <td className="wd-tabular">{r.samplingReserved}</td>
              <td className="wd-tabular">{r.expectedWon}</td>
              <td className="wd-tabular">{r.available}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {report.copyPreviews.length ? (
        <details>
          <summary>Copy previews ({report.copyPreviews.length})</summary>
          <ul>
            {report.copyPreviews.map((c) => (
              <li key={`${c.key}-${c.locale}`}>
                <code>{c.key}</code> ({c.locale}): {c.text}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
      <p className="wd-muted">Generated {intl.formatDateTime(report.generatedAt)}.</p>
    </div>
  );
}
